/**
 * ResearchWorkspace.jsx — the Research section (HANDOFF §5.1).
 *
 * Routes: #research/overview · #research/strategies · #research/strategies/:key
 *         #research/papers · #research/inputs · #research/ops
 *
 * The seven legacy tabs are untouched and still reachable with ?legacy=1 — the
 * switch lives in the caller, not here.
 */

import React, { useCallback, useEffect, useState } from "react";

import { ResearchDataProvider } from "../../lib/researchApi";
import ResearchPapers from "./ResearchPapers";
import ResearchOverview from "./ResearchOverview";
import ResearchStrategies from "./ResearchStrategies";
import { ResearchInputs, ResearchOps } from "./ResearchInputsOps";
import "./research.css";

const TABS = [
  ["overview", "Overview", "The verdict"],
  ["strategies", "Strategies", "One table, one bar"],
  ["papers", "Papers", "Research library and integration briefs"],
  ["inputs", "Inputs", "Feeds and market signals"],
  ["ops", "Ops", "NEXUS, OMEGA and the desk"],
];

const PREFIX = "#research/";

function readRoute() {
  if (typeof window === "undefined") return { tab: "overview", key: null };
  const h = window.location.hash || "";
  if (!h.startsWith(PREFIX)) return { tab: "overview", key: null };
  const rest = h.slice(PREFIX.length);
  const [tab, ...tail] = rest.split("/");
  const known = TABS.some(([id]) => id === tab);
  return {
    tab: known ? tab : "overview",
    key: tail.length ? decodeURIComponent(tail.join("/")) : null,
  };
}

export default function ResearchWorkspace() {
  const [route, setRoute] = useState(readRoute);

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback((tab, key) => {
    const next = `${PREFIX}${tab}${key ? `/${encodeURIComponent(key)}` : ""}`;
    if (window.location.hash !== next) window.location.hash = next;
    else setRoute(readRoute());
  }, []);

  return (
    <ResearchDataProvider>
      <div className="rx">
        <nav className="rx-row" style={{ gap: 6, marginBottom: 16 }} aria-label="Research sections">
          {TABS.map(([id, label, hint]) => (
            <button key={id} type="button" className="rx-chip"
                    aria-pressed={route.tab === id}
                    title={hint}
                    style={{ minHeight: 44, padding: "0 16px" }}
                    onClick={() => go(id)}>
              {label}
            </button>
          ))}
        </nav>

        {route.tab === "overview" && (
          <ResearchOverview onGoToStrategies={() => go("strategies")} />
        )}
        {route.tab === "strategies" && (
          <ResearchStrategies initialKey={route.key}
                              onSelect={(key) => go("strategies", key)} />
        )}
        {route.tab === "papers" && <ResearchPapers />}
        {route.tab === "inputs" && <ResearchInputs />}
        {route.tab === "ops" && <ResearchOps />}
      </div>
    </ResearchDataProvider>
  );
}
